/**
 * Blog Post Toolbar
 *
 * Handles text resize dropdown, share dropdown, and outside-click dismissal.
 */
class BlogPostToolbar extends HTMLElement {
	#baseFontSizes = null;

	connectedCallback() {
		requestAnimationFrame(() => this.#init());
	}

	#init() {
		this.#initResize();
		this.#initShare();
		this.#initOutsideClick();
	}

	// ── Resize ──────────────────────────────────────────────
	#initResize() {
		const toggle = this.querySelector('[ref="resizeToggle"]');
		const menu = this.querySelector('[ref="resizeMenu"]');
		if (!toggle || !menu) return;

		toggle.addEventListener('click', () => this.#toggleMenu(toggle, menu));

		menu.querySelectorAll('[data-size]').forEach((btn) => {
			btn.addEventListener('click', () => {
				const size = btn.dataset.size;
				this.#applyTextSize(size);

				// Update active state
				menu.querySelectorAll('.blog-post-toolbar__menu-item').forEach((b) => b.classList.remove('is-active'));
				btn.classList.add('is-active');
				this.#closeMenu(toggle, menu);
			});
		});
	}

	/**
	 * @param {string} size
	 */
	#applyTextSize(size) {
		const content = document.querySelector('.blog-post-content');
		if (!content) return;

		const scales = { small: 0, medium: 1.15, large: 1.3 };
		const scale = scales[size] ?? 0;

		// Capture computed font sizes once, before any inline overrides are applied.
		// Using per-element px values avoids relying on calc(1em * scale) which
		// doesn't cascade through children that have explicit font-size tokens on mobile.
		if (!this.#baseFontSizes) {
			this.#baseFontSizes = new Map();
			[content, ...content.querySelectorAll('*')].forEach((el) => {
				this.#baseFontSizes.set(el, parseFloat(getComputedStyle(el).fontSize));
			});
		}

		if (!scale) {
			this.#baseFontSizes.forEach((_, el) => el.style.removeProperty('font-size'));
			return;
		}

		this.#baseFontSizes.forEach((baseSize, el) => {
			el.style.setProperty('font-size', `${baseSize * scale}px`, 'important');
		});
	}

	// ── Share ───────────────────────────────────────────────
	#initShare() {
		const toggle = this.querySelector('[ref="shareToggle"]');
		const menu = this.querySelector('[ref="shareMenu"]');
		if (!toggle || !menu) return;

		toggle.addEventListener('click', () => this.#toggleMenu(toggle, menu));

		menu.querySelectorAll('[data-share]').forEach((btn) => {
			btn.addEventListener('click', () => {
				this.#share(btn.dataset.share);
				this.#closeMenu(toggle, menu);
			});
		});
	}

	/**
	 * @param {string} platform
	 */
	#share(platform) {
		const url = encodeURIComponent(window.location.href);
		const title = encodeURIComponent(document.title);

		const urls = {
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
			twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
			email: `mailto:?subject=${title}&body=${url}`,
			copy: null,
		};

		if (platform === 'copy') {
			navigator.clipboard.writeText(window.location.href).then(() => {
				const btn = this.querySelector('[data-share="copy"]');
				if (btn) {
					const original = btn.textContent;
					btn.textContent = 'Copied!';
					setTimeout(() => (btn.textContent = original), 2000);
				}
			});
			return;
		}

		const shareUrl = urls[platform];
		if (shareUrl) {
			window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
		}
	}

	// ── Dropdown helpers ────────────────────────────────────
	/**
	 * @param {HTMLElement} toggle
	 * @param {HTMLElement} menu
	 */
	#toggleMenu(toggle, menu) {
		const isOpen = menu.classList.contains('is-open');
		// Close all menus first
		this.querySelectorAll('.blog-post-toolbar__menu').forEach((m) => m.classList.remove('is-open'));
		this.querySelectorAll('[aria-expanded]').forEach((t) => t.setAttribute('aria-expanded', 'false'));

		if (!isOpen) {
			menu.classList.add('is-open');
			toggle.setAttribute('aria-expanded', 'true');
		}
	}

	/**
	 * @param {HTMLElement} toggle
	 * @param {HTMLElement} menu
	 */
	#closeMenu(toggle, menu) {
		menu.classList.remove('is-open');
		toggle.setAttribute('aria-expanded', 'false');
	}

	#initOutsideClick() {
		document.addEventListener('click', (e) => {
			if (!this.contains(e.target)) {
				this.querySelectorAll('.blog-post-toolbar__menu').forEach((m) => m.classList.remove('is-open'));
				this.querySelectorAll('[aria-expanded]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
			}
		});
	}
}

if (!customElements.get('blog-post-toolbar')) {
	customElements.define('blog-post-toolbar', BlogPostToolbar);
}
